import { Editor } from "@monaco-editor/react";
import PsychologyIcon from "@mui/icons-material/Psychology";
import {
  Box,
  Button,
  Card,
  CardContent,
  capitalize,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { TestContext } from "../types";
import { humanizeText } from "../utils/text";

type DetailsProps = {
  ix?: number;
  context: TestContext;
  onRemove?: (context: TestContext) => void;
};

export const TextContextDetails = ({ ix, context, onRemove }: DetailsProps) => {
  const block = context.data;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="column" alignItems="flex-start" justifyContent="stretch" spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="subtitle2">
              {ix !== undefined ? `${ix + 1}.` : ""} {context.name} ({block?.action?.action ?? "?"})
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              divider={<Typography variant="body2">·</Typography>}
            >
              <Tooltip title="Confidence Level">
                <Typography variant="caption" color="text.secondary">
                  {block?.confidence ? humanizeText(block?.confidence) : ""}
                </Typography>
              </Tooltip>
              <Tooltip title="AAA Phase">
                <Typography variant="caption" color="text.secondary">
                  {capitalize(block?.aaa_phase ?? "N/A")}
                </Typography>
              </Tooltip>
              {block?.reasoning && (
                <Tooltip title={block?.reasoning ?? "No reasoning provided"}>
                  <PsychologyIcon color="info" sx={{ cursor: "pointer" }} />
                </Tooltip>
              )}
            </Stack>
          </Stack>
          {context.description && <Typography variant="body2">{context.description}</Typography>}
        </Stack>
        {block?.code && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Editor
              height="50px"
              defaultLanguage="python"
              value={block.code}
              options={{
                readOnly: true,
                lineNumbers: "off",
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
              }}
            />
          </Box>
        )}
        <Divider sx={{ mt: 2, mb: 1 }} />
        {context.end_goal && (
          <Typography variant="body2" color="text.secondary">
            End goal is to "{context.end_goal}"
          </Typography>
        )}
        {block?.original_task && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            The original task was "{block.original_task}"
          </Typography>
        )}
        {onRemove && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Tooltip title="Remove this step from the test">
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => onRemove(context)}
              >
                Remove
              </Button>
            </Tooltip>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

type Props = {
  contexts: TestContext[];
  onRemove?: (context: TestContext) => void;
};

export const TestContextList = ({ contexts, onRemove }: Props) => {
  if (contexts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No contexts available.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {contexts.map((ctx, index) => (
        <TextContextDetails key={ctx.id} ix={index} context={ctx} onRemove={onRemove} />
      ))}
    </Stack>
  );
};
